import { useEffect } from "react";
import { analytics } from "../lib/posthog";
import { useAuthStore } from "../stores/useAuthStore";
import { useOrganizationStore } from "../stores/useOrganizationStore";

/**
 * Attributes subsequent PostHog events to the current user, their active
 * organization, and their active facility. Product wants engagement measured
 * per individual user AND per customer org (with role-level breakdown within
 * an org), not just one — see
 * docs/superpowers/specs/2026-08-17-posthog-integration-design.md.
 *
 * Beyond identify()/group() (which set the person/group profile), product
 * also wants email/username/role/organization/facility attached to every
 * event, not just derivable by joining against the person profile —
 * so each of these is also registered as a PostHog "super property"
 * (analytics.register), which PostHog auto-attaches to every subsequent
 * event, including autocapture, until reset() clears it on logout.
 *
 * Resetting identity on logout is handled separately, inside
 * useAuthStore's clearAuth() (covers every logout path — button, token
 * refresh failure, SSE/API 401 — in one place); reset() also clears
 * registered super properties.
 */
export function usePostHogIdentity(): void {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);

  useEffect(() => {
    if (!isAuthenticated || !user) return;
    analytics.identify(user);
    analytics.register({
      email: user.email,
      // AuthUser has no dedicated "username" field — full_name is the
      // closest match and what product means by "username" here.
      username: user.full_name ?? user.fullName,
      role: user.platformRole ?? user.role,
    });
  }, [isAuthenticated, user]);

  const activeOrgId = useOrganizationStore((state) => state.activeOrgId);
  const organizations = useOrganizationStore((state) => state.organizations);

  useEffect(() => {
    if (activeOrgId == null) return;
    const org = organizations.find((o) => o.id === activeOrgId);
    if (!org) return;
    analytics.group("organization", org.id, { name: org.name });
    analytics.register({ organization: org.name, organization_id: org.id });
  }, [activeOrgId, organizations]);

  const activeFacilityId = useOrganizationStore((state) => state.activeFacilityId);
  const activeFacilityName = useOrganizationStore((state) => state.activeFacilityName);

  useEffect(() => {
    if (!activeFacilityId) return;
    analytics.register({ facility: activeFacilityName, facility_id: activeFacilityId });
  }, [activeFacilityId, activeFacilityName]);
}
